import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import jsPDF from 'jspdf';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';

const MyAbsenceRequests = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [establishmentInfo, setEstablishmentInfo] = useState(null);

  // Charger les informations de l'établissement
  const fetchEstablishmentInfo = async () => {
    try {
      if (userData?.establishmentId) {
        const establishmentRef = doc(db, 'establishments', userData.establishmentId);
        const establishmentSnap = await getDoc(establishmentRef);
        if (establishmentSnap.exists()) {
          setEstablishmentInfo(establishmentSnap.data());
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des informations de l\'établissement:', error);
    }
  };

  // Fonction pour convertir Timestamp en date lisible
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date invalide';
    if (timestamp instanceof Timestamp) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString('fr-FR');
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR');
    }
    return 'Date invalide';
  };

  // Charger mes demandes d'absence
  const fetchMyAbsences = async () => {
    try {
      setLoading(true);
      
      // Récupérer les absences où le chef de service est le personnel (ses propres demandes)
      const absencesQuery = query(
        collection(db, 'absences'),
        where('personnelId', '==', currentUser.uid),
        where('chefServiceId', '==', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(absencesQuery);
      
      const absencesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dateDebutFormatted: formatDate(data.dateDebut),
          dateFinFormatted: formatDate(data.dateFin),
        };
      });

      // Trier par date de création (plus récentes en premier)
      absencesData.sort((a, b) => {
        const dateA = a.createdAt?.seconds || 0;
        const dateB = b.createdAt?.seconds || 0;
        return dateB - dateA;
      });

      setAbsences(absencesData);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des absences:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && userData) {
      fetchEstablishmentInfo();
      fetchMyAbsences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userData]);

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'en attente':
        return 'warning';
      case 'approuvée':
        return 'success';
      case 'refusée':
        return 'error';
      case 'signalée':
        return 'info';
      default:
        return 'default';
    }
  };

  // Fonction pour obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'en attente':
        return <PendingIcon />;
      case 'approuvée':
        return <CheckCircleIcon />;
      case 'refusée':
        return <CancelIcon />;
      default:
        return null;
    }
  };

  // Fonction pour imprimer une demande d'absence
  const handlePrintAbsence = (absence) => {
    try {
      // Créer un nouveau document PDF au format A4
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Définir les marges et positions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;
      
      // Ajouter le logo à gauche
      if (establishmentInfo?.logo) {
        try {
          // Si le logo est déjà au format base64 (commence par 'data:image/')
          if (establishmentInfo.logo.startsWith('data:image/')) {
            // Calculer les dimensions pour garder le ratio
            const logoWidth = 25;
            const logoHeight = 15;
            doc.addImage(establishmentInfo.logo, 'AUTO', margin, yPos, logoWidth, logoHeight);
            
            // Ajouter le logo à droite aussi
            doc.addImage(establishmentInfo.logo, 'AUTO', pageWidth - margin - logoWidth, yPos, logoWidth, logoHeight);
            
            // Décaler le texte pour ne pas chevaucher le logo
            yPos += 5;
          }
        } catch (logoError) {
          console.error('Erreur lors de l\'ajout du logo:', logoError);
          // Continuer sans logo en cas d'erreur
        }
      }
      
      // En-tête avec nom de l'établissement
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(0, 102, 204); // Bleu
      const schoolName = establishmentInfo?.name || establishmentInfo?.nom || 'LYCÉE';
      doc.text(schoolName, pageWidth / 2, yPos + 10, { align: 'center' });
      
      // Adresse de l'établissement
      yPos += 15;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0); // Noir
      const address = establishmentInfo?.address || '';
      doc.text(address, pageWidth / 2, yPos, { align: 'center' });
      
      // Ligne de séparation
      yPos += 10;
      doc.setDrawColor(0, 102, 204); // Bleu
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      
      // Titre du document
      yPos += 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(0, 102, 204); // Bleu
      doc.text('DEMANDE D\'ABSENCE', pageWidth / 2, yPos, { align: 'center' });
      
      // Informations de la demande d'absence
      yPos += 15;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0); // Noir
      
      // Tableau d'informations
      const tableData = [
        ['Service / Département:', userData?.service || userData?.department || 'Non spécifié'],
        ['Nom et Prénom:', `${userData?.firstName} ${userData?.lastName}`],
        ['Fonction:', 'Chef de Service'],
        ['Type d\'absence:', absence.type === 'autre' && absence.autreType ? absence.autreType : absence.type],
        ['Date de début:', absence.dateDebutFormatted],
        ['Date de fin:', absence.dateFinFormatted],
        ['Durée:', absence.touteLaJournee ? 'Toute la journée' : 
          (absence.heureDebut && absence.heureFin ? 
            `${absence.heureDebut} - ${absence.heureFin} (${absence.nombreHeures || 'Non spécifié'} heure(s))` : 
            `${absence.nombreHeures || 'Non spécifié'} heure(s)`
          )
        ],
        ['Motif:', absence.motif || 'Non spécifié']
      ];
      
      // Ajouter le statut si la demande a été validée
      if (absence.statut !== 'en attente') {
        tableData.push(['Statut:', absence.statut.toUpperCase()]);
        if (absence.validatedAt) {
          tableData.push(['Date de validation:', formatDate(absence.validatedAt)]);
        }
        if (absence.validationComment) {
          tableData.push(['Commentaire:', absence.validationComment]);
        }
      }
      
      // Dessiner le tableau
      const colWidth = [(pageWidth - 2 * margin) * 0.4, (pageWidth - 2 * margin) * 0.6];
      const rowHeight = 10;
      
      tableData.forEach((row, i) => {
        // Fond gris pour les labels
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, yPos, colWidth[0], rowHeight, 'F');
        
        // Bordures
        doc.setDrawColor(200, 200, 200);
        doc.rect(margin, yPos, colWidth[0], rowHeight, 'S');
        doc.rect(margin + colWidth[0], yPos, colWidth[1], rowHeight, 'S');
        
        // Texte
        doc.setFont('helvetica', 'bold');
        doc.text(row[0], margin + 2, yPos + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.text(row[1], margin + colWidth[0] + 2, yPos + 6);
        
        yPos += rowHeight;
      });
      
      // Espace pour les signatures
      yPos += 30;
      
      // Signature du Chef de Service
      doc.setFont('helvetica', 'bold');
      doc.text('Signature du Chef de Service', margin + 25, yPos, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text(`${userData?.firstName} ${userData?.lastName}`, margin + 25, yPos + 7, { align: 'center' });
      doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, margin + 25, yPos + 14, { align: 'center' });
      doc.line(margin, yPos - 5, margin + 50, yPos - 5); // Ligne pour signature
      
      // Signature de l'Administration
      doc.setFont('helvetica', 'bold');
      doc.text('Signature de l\'Administration', pageWidth - margin - 25, yPos, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.text('', pageWidth - margin - 25, yPos + 7, { align: 'center' });
      doc.text(`Date: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin - 25, yPos + 14, { align: 'center' });
      doc.line(pageWidth - margin - 50, yPos - 5, pageWidth - margin, yPos - 5); // Ligne pour signature
      
      // Pied de page
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, pageWidth / 2, pageHeight - 15, { align: 'center' });
      
      // Ajouter une ligne de séparation pour le pied de page
      doc.setDrawColor(200, 200, 200); // Gris clair
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      
      // Ajouter "Edugestion.softmarket.io" en bas au centre
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100); // Gris foncé
      doc.text('Edugestion.softmarket.io', pageWidth / 2, pageHeight - 7, { align: 'center' });
      
      // Générer et télécharger le PDF
      const fileName = `Demande_Absence_${userData?.firstName}_${userData?.lastName}_${absence.dateDebutFormatted.replace(/\//g, '-')}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Une erreur est survenue lors de la génération du PDF');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/chef-service/dashboard')}
          sx={{ mb: 2 }}
        >
          Retour au tableau de bord
        </Button>

        <Typography variant="h4" gutterBottom>
          Mes Demandes d'Absence
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Consultez et imprimez vos demandes d'autorisation d'absence
        </Typography>
      </Box>

      {absences.length === 0 ? (
        <Alert severity="info">
          Vous n'avez aucune demande d'absence enregistrée.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Date de début</strong></TableCell>
                <TableCell><strong>Date de fin</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Motif</strong></TableCell>
                <TableCell><strong>Statut</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {absences.map((absence) => (
                <TableRow key={absence.id}>
                  <TableCell>{absence.dateDebutFormatted}</TableCell>
                  <TableCell>{absence.dateFinFormatted}</TableCell>
                  <TableCell>
                    {absence.type === 'autre' && absence.autreType 
                      ? absence.autreType 
                      : absence.type}
                  </TableCell>
                  <TableCell>
                    {absence.motif?.length > 50 
                      ? `${absence.motif.substring(0, 50)}...` 
                      : absence.motif}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(absence.statut)}
                      label={absence.statut}
                      color={getStatusColor(absence.statut)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handlePrintAbsence(absence)}
                      title="Imprimer"
                    >
                      <PrintIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default MyAbsenceRequests;
